import React from "react";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

/**
 * Section « Récurrence » réutilisable — mêmes options que la modale
 * « Nouvelle plage » de l'Agenda départements (AgendaSlotDialog).
 *
 * Trois modes :
 *   - once     : ponctuelle (une seule date)
 *   - weekly   : hebdomadaire (jours de semaine + toutes les N semaines)
 *   - monthly  : mensuelle (jours du mois + tous les N mois)
 *
 * Props :
 *   value    : { recurrence_type, recurrence_days, recurrence_interval, end_date }
 *   onChange : (partial) => void
 *   startDate: string YYYY-MM-DD  (date de départ, non gérée ici, utile pour
 *              pré-cocher automatiquement le jour de semaine correspondant)
 *   testIdPrefix : préfixe pour les data-testid (ex. "shift-rec", "interv-rec")
 */
const WEEKDAYS = [
  { v: 0, l: "Lun" },
  { v: 1, l: "Mar" },
  { v: 2, l: "Mer" },
  { v: 3, l: "Jeu" },
  { v: 4, l: "Ven" },
  { v: 5, l: "Sam" },
  { v: 6, l: "Dim" },
];

export const RECURRENCE_EMPTY = {
  recurrence_type: "once",
  recurrence_days: [],
  recurrence_interval: 1,
  end_date: "",
};

/**
 * Traduit une config `RECURRENCE_EMPTY` en payload accepté par les endpoints
 * backend `POST /shifts/{id}/recurrence` et `POST /department-agenda/{id}/recurrence`.
 * Retourne null si aucune récurrence à appliquer (type=once ou pas de end_date).
 */
export function toRecurrencePayload(config) {
  if (!config || config.recurrence_type === "once" || !config.end_date) return null;
  const payload = {
    frequency: config.recurrence_type, // "weekly" | "monthly"
    end_date: config.end_date,
    interval: Math.max(1, Number(config.recurrence_interval) || 1),
  };
  if (config.recurrence_type === "weekly") {
    payload.weekdays = Array.isArray(config.recurrence_days) ? [...config.recurrence_days] : [];
  } else if (config.recurrence_type === "monthly") {
    payload.monthly_days = Array.isArray(config.recurrence_days) ? [...config.recurrence_days] : [];
  }
  return payload;
}

export default function RecurrenceFormSection({
  value,
  onChange,
  startDate,
  testIdPrefix = "rec",
  disabled = false,
}) {
  const v = value || RECURRENCE_EMPTY;
  const set = (patch) => onChange({ ...v, ...patch });

  const toggleDay = (d) => {
    const list = v.recurrence_days || [];
    const next = list.includes(d) ? list.filter((x) => x !== d) : [...list, d].sort((a, b) => a - b);
    set({ recurrence_days: next });
  };

  return (
    <div className="border rounded-sm border-zinc-200 p-3 space-y-3 bg-zinc-50/40" data-testid={`${testIdPrefix}-section`}>
      <div>
        <Label className="label-uppercase mb-1.5 block">Récurrence</Label>
        <Select
          value={v.recurrence_type}
          onValueChange={(val) => set({ recurrence_type: val, recurrence_days: [] })}
          disabled={disabled}
        >
          <SelectTrigger data-testid={`${testIdPrefix}-type`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="once">Ponctuelle (une seule date)</SelectItem>
            <SelectItem value="weekly">Hebdomadaire</SelectItem>
            <SelectItem value="monthly">Mensuelle</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {v.recurrence_type === "weekly" && (
        <div>
          <Label className="label-uppercase mb-1.5 block">Jours de la semaine</Label>
          <div className="flex gap-1.5 flex-wrap" data-testid={`${testIdPrefix}-weekdays`}>
            {WEEKDAYS.map((d) => {
              const active = (v.recurrence_days || []).includes(d.v);
              return (
                <button
                  key={d.v}
                  type="button"
                  disabled={disabled}
                  onClick={() => toggleDay(d.v)}
                  data-testid={`${testIdPrefix}-weekday-${d.v}`}
                  className={`px-2.5 py-1 text-xs font-medium rounded-sm border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    active
                      ? "bg-[#7C3AED] border-[#7C3AED] text-white"
                      : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-400"
                  }`}
                >{d.l}</button>
              );
            })}
          </div>
          {startDate && (v.recurrence_days || []).length === 0 && (
            <p className="text-[10px] text-zinc-500 mt-1 font-mono">
              Si aucun jour sélectionné, le même jour de la semaine que la date de début sera utilisé.
            </p>
          )}
        </div>
      )}

      {v.recurrence_type === "monthly" && (
        <div>
          <Label className="label-uppercase mb-1.5 block">Jours du mois (1 → 31)</Label>
          <div className="grid grid-cols-10 gap-1" data-testid={`${testIdPrefix}-monthdays`}>
            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => {
              const active = (v.recurrence_days || []).includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  disabled={disabled}
                  onClick={() => toggleDay(d)}
                  data-testid={`${testIdPrefix}-monthday-${d}`}
                  className={`text-[11px] font-mono py-1 rounded-sm border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    active
                      ? "bg-[#7C3AED] border-[#7C3AED] text-white"
                      : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-400"
                  }`}
                >{d}</button>
              );
            })}
          </div>
          {startDate && (v.recurrence_days || []).length === 0 && (
            <p className="text-[10px] text-zinc-500 mt-1 font-mono">
              Si aucun jour sélectionné, le même quantième que la date de début sera utilisé.
            </p>
          )}
        </div>
      )}

      {v.recurrence_type !== "once" && (
        <div className="grid grid-cols-2 gap-3 items-end">
          <div>
            <Label className="label-uppercase mb-1.5 block">
              {v.recurrence_type === "weekly" ? "Toutes les N semaines" : "Tous les N mois"}
            </Label>
            <Input
              type="number"
              min={1}
              value={v.recurrence_interval || 1}
              onChange={(e) => set({ recurrence_interval: Math.max(1, parseInt(e.target.value || "1", 10)) })}
              className="w-24 rounded-sm"
              disabled={disabled}
              data-testid={`${testIdPrefix}-interval`}
            />
          </div>
          <div>
            <Label className="label-uppercase mb-1.5 block">Date de fin</Label>
            <Input
              type="date"
              value={v.end_date || ""}
              min={startDate || undefined}
              onChange={(e) => set({ end_date: e.target.value })}
              className="rounded-sm"
              disabled={disabled}
              data-testid={`${testIdPrefix}-end-date`}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Expanse une configuration de récurrence en liste de dates ISO à créer.
 *
 * Retourne la liste des dates *strictement postérieures* à `startDate`
 * (la date de départ correspond à la source, déjà créée par ailleurs).
 *
 * @param {string} startDate   YYYY-MM-DD — date de la source (déjà créée)
 * @param {object} config      { recurrence_type, recurrence_days, recurrence_interval, end_date }
 * @returns {string[]} dates ISO
 */
export function expandRecurrenceDates(startDate, config) {
  if (!startDate || !config || config.recurrence_type === "once") return [];
  const { recurrence_type, recurrence_days, recurrence_interval, end_date } = config;
  if (!end_date) return [];
  const start = new Date(startDate + "T00:00:00");
  const end = new Date(end_date + "T00:00:00");
  if (isNaN(start) || isNaN(end) || end <= start) return [];
  const interval = Math.max(1, Number(recurrence_interval) || 1);
  const out = [];

  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  if (recurrence_type === "weekly") {
    // Jours de semaine choisis (0=Lun..6=Dim). Si vide, on prend le même que start.
    // Note : getDay() renvoie 0=Dim..6=Sam → conversion → (getDay()+6)%7 = 0=Lun..6=Dim
    let wds = Array.isArray(recurrence_days) && recurrence_days.length > 0
      ? [...new Set(recurrence_days)].sort((a, b) => a - b)
      : [(start.getDay() + 6) % 7];
    // Début : lundi de la semaine de start
    const weekStart = new Date(start);
    const dayIdx = (start.getDay() + 6) % 7;
    weekStart.setDate(weekStart.getDate() - dayIdx);
    while (true) {
      // Génère les jours cette semaine
      let anyInRange = false;
      for (const wd of wds) {
        const cand = new Date(weekStart);
        cand.setDate(cand.getDate() + wd);
        if (cand > end) continue;
        if (cand <= start) continue; // ignorer les dates <= start (la source est déjà là)
        out.push(fmt(cand));
        anyInRange = true;
      }
      // Avance d'`interval` semaines
      weekStart.setDate(weekStart.getDate() + 7 * interval);
      if (weekStart > end && !anyInRange) break;
      if (weekStart > end) break;
    }
  } else if (recurrence_type === "monthly") {
    // Jours du mois choisis (1..31). Si vide, on prend le quantième de start.
    const days = Array.isArray(recurrence_days) && recurrence_days.length > 0
      ? [...new Set(recurrence_days)].sort((a, b) => a - b)
      : [start.getDate()];
    // Début : premier du mois de start
    let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cursor <= end) {
      for (const d of days) {
        const cand = new Date(cursor.getFullYear(), cursor.getMonth(), d);
        // Si le quantième dépasse le nombre de jours du mois, JS "roule" au mois suivant
        // → on filtre les dates dont le mois n'est plus celui du curseur
        if (cand.getMonth() !== cursor.getMonth()) continue;
        if (cand <= start) continue; // ignorer les dates <= start
        if (cand > end) continue;
        out.push(fmt(cand));
      }
      // Avance de `interval` mois
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + interval, 1);
    }
  }

  return [...new Set(out)].sort();
}
